/**
 * 80 UI fiscal-benefit credit (Decreto 206/019) — THE source of truth.
 *
 * This lives in shared-utils rather than in the backend because three surfaces
 * need the same number and they had already drifted apart:
 *   - apps/backend  (the credit we grant and recover on Form 2181)
 *   - apps/landing  (the "con beneficio" price shown to prospects)
 *   - scripts/dlocal-create-plans.ts (the amount baked into dLocal plan objects)
 *
 * Before this module the backend said 514 while the landing and the plan script
 * both said 513. That drift matters more than one peso suggests: the yearly
 * revaluation mechanism works by updating the table below and then PATCHing the
 * dLocal plan amounts, so a plan script holding its own constant would silently
 * fail to move the plans — the exact failure the mechanism exists to prevent.
 *
 * The value is 80 x (valor de la Unidad Indexada), which DGI revalues each year.
 * Add a new entry every January. When a year is missing, the non-strict lookup
 * falls back to the most recent known year so billing stays alive with a slightly
 * stale number rather than crashing; use the strict lookup wherever silently
 * charging last year's figure would be worse than failing loudly.
 *
 * The benefit WINDOW is data below rather than a sentence here, because "is the
 * benefit still running?" is a question the backend has to answer at runtime:
 * root can extend the programme from the portal without a deploy. Everything
 * here stays pure and takes the window as a parameter, so the configured value
 * and the default below go through identical logic — there is no second code
 * path that only runs in production.
 *
 * @see https://www.gub.uy/direccion-general-impositiva
 */
/**
 * Zero-padded `YYYY-MM` with a real month.
 *
 * The padding is not cosmetic. Every window comparison here is lexicographic, so
 * `2026-9` sorts ABOVE `2026-12` and reads as outside the window — silently, with
 * nothing thrown. Machine-generated periods all pad (`padStart(2, '0')`), so this
 * matters wherever a period can come from a human.
 */
export function isValidFiscalPeriod(period) {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(period);
}
/**
 * The window with nothing configured: Decreto 206/019, extended once to
 * 31/12/2026 by Decreto 419/023.
 *
 * This is the FALLBACK, not the authority. A database with no window configured
 * must behave exactly as this code did before the value became configurable, so
 * that an unset key can never be an outage.
 */
export const DEFAULT_FISCAL_BENEFIT_WINDOW = {
    firstPeriod: '2019-11',
    lastPeriod: '2026-12',
};
/** The decreto new declarations are stamped with when none is configured. */
export const DEFAULT_FISCAL_BENEFIT_DECRETO = '206/019';
/** Whether the benefit applies to a `YYYY-MM` period. */
export function isFiscalBenefitActiveForPeriod(period, window = DEFAULT_FISCAL_BENEFIT_WINDOW) {
    return period >= window.firstPeriod && period <= window.lastPeriod;
}
/**
 * Whether the benefit applies to ANY period of a calendar year.
 *
 * Deliberately "any", not "all": the programme starts and ends mid-year (it
 * began 2019-11 and the current window ends 2026-12), so a year the benefit ran
 * for two months is still a year we grant, declare and prompt for. Asking "all"
 * would silently drop 2019.
 */
export function isFiscalBenefitActiveForYear(year, window = DEFAULT_FISCAL_BENEFIT_WINDOW) {
    return (`${year}-12` >= window.firstPeriod && `${year}-01` <= window.lastPeriod);
}
/**
 * 80 UI monthly credit in UYU MINOR units (cents), by calendar year.
 *
 * 2026 = 51400 (UYU 514.00): DGI's own published headline figure — 80 x UI
 * 6.4237 as of 01/01/2026 = 513.90, which DGI rounds to 514.
 */
const FISCAL_BENEFIT_80UI_MINOR_BY_YEAR = {
    2026: 51400,
};
/** Years we hold an explicit value for, newest first. */
export function knownFiscalBenefit80UiYears() {
    return Object.keys(FISCAL_BENEFIT_80UI_MINOR_BY_YEAR)
        .map(Number)
        .sort((a, b) => b - a);
}
/**
 * Strict lookup — null when the year is not in the table.
 *
 * Use this when falling back to a stale figure would be worse than failing: the
 * yearly dLocal plan-amount update, for instance, should refuse to run rather
 * than quietly re-apply last year's credit to this year's plans.
 */
export function getFiscalBenefit80UiMinorForYearStrict(year) {
    return FISCAL_BENEFIT_80UI_MINOR_BY_YEAR[year] ?? null;
}
/**
 * Lenient lookup — falls back to the most recent known year.
 *
 * Use this on request paths where a slightly stale credit beats a hard failure.
 */
export function getFiscalBenefit80UiMinorForYear(year) {
    const exact = FISCAL_BENEFIT_80UI_MINOR_BY_YEAR[year];
    if (exact != null)
        return exact;
    const newest = knownFiscalBenefit80UiYears()[0];
    if (newest == null) {
        throw new Error('FISCAL_BENEFIT_80UI_MINOR_BY_YEAR is empty');
    }
    return FISCAL_BENEFIT_80UI_MINOR_BY_YEAR[newest];
}
/**
 * The credit in whole UYU (major units) for a year — for display surfaces and
 * for the dLocal plan script, which both work in pesos rather than cents.
 */
export function getFiscalBenefit80UiMajorForYear(year) {
    return getFiscalBenefit80UiMinorForYear(year) / 100;
}
